import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { elevationBand, formatElevationMeters } from '@radevents/shared';

interface GpxMapProps {
  gpxUrl: string;
  color?: string;
  height?: number;
  /** Kept for backwards-compat; the surrounding card renders the STRECKE header now. */
  title?: string;
  routeUrl?: string;
  /**
   * Kennzahlen der geladenen Datei, sobald sie geparst ist. Damit kann der Aufrufer sie mit
   * anderen Angaben vergleichen (Pruefansicht: GPX gegen die vorgeschlagene Distanz und den
   * Eventort) — ohne die Datei ein zweites Mal zu parsen.
   */
  onStats?: (stats: GpxStats) => void;
  /**
   * Schmales Höhenprofil ohne Kopfzeile (Kopf der Event-Seite auf dem Handy): Karte, Profil und
   * Streckenwahl darunter passen so gemeinsam in den ersten Bildschirm.
   */
  compactProfile?: boolean;
  /**
   * Kopfzeile „Höhenprofil · km · hm“ über dem Profil. Aus, wo die Kennzahlen schon über der
   * Karte stehen (Event-Seite am Rechner); sonst stünden sie zweimal da, einmal gemessen und
   * einmal angegeben.
   */
  profileHeader?: boolean;
}

export interface GpxStats {
  distanceKm: number;
  ascentM: number;
  start: { lat: number; lon: number };
  end: { lat: number; lon: number };
}

interface TrackPoint {
  lat: number;
  lon: number;
  ele: number | null;
}

function parseGpx(xml: string): TrackPoint[] {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const trkpts = doc.getElementsByTagName('trkpt');
  const points: TrackPoint[] = [];
  for (let i = 0; i < trkpts.length; i++) {
    const el = trkpts[i];
    const lat = parseFloat(el.getAttribute('lat') ?? '');
    const lon = parseFloat(el.getAttribute('lon') ?? '');
    if (Number.isNaN(lat) || Number.isNaN(lon)) continue;
    const eleEl = el.getElementsByTagName('ele')[0];
    const ele = eleEl ? parseFloat(eleEl.textContent ?? '') : NaN;
    points.push({ lat, lon, ele: Number.isFinite(ele) ? ele : null });
  }
  if (points.length === 0) {
    const rtepts = doc.getElementsByTagName('rtept');
    for (let i = 0; i < rtepts.length; i++) {
      const el = rtepts[i];
      const lat = parseFloat(el.getAttribute('lat') ?? '');
      const lon = parseFloat(el.getAttribute('lon') ?? '');
      if (!Number.isNaN(lat) && !Number.isNaN(lon)) points.push({ lat, lon, ele: null });
    }
  }
  return points;
}

function cumulativeDistancesKm(points: TrackPoint[]): number[] {
  const R = 6371;
  const out = [0];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLon = (b.lon - a.lon) * Math.PI / 180;
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    out.push(out[i - 1] + R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)));
  }
  return out;
}

/** Round to a "nice" tick step given a value range and target tick count. */
function niceStep(range: number, targetTicks: number): number {
  const rough = range / targetTicks;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const norm = rough / pow;
  const step = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  return step * pow;
}

// Design tokens (Rad Events redesign)
const ACCENT = '#922C57';
const AREA_FILL = 'rgba(146,44,87,0.12)';
const GRID = 'rgba(38,38,38,0.06)';
const AXIS_LABEL = '#6A6A6A';
const INK = '#2C2C2C';
const BORDER = '#E8E4F0';
const MAP_BG = '#EEEBF5';
const AXIS_FONT = "10px 'Space Mono', monospace";

const ELEV_PAD_LEFT = 4;
const ELEV_PAD_RIGHT = 40;        // room for elevation labels on the right
const ELEV_PAD_TOP = 8;
const ELEV_PAD_BOTTOM = 18;       // room for km labels at the bottom

const GpxMap: React.FC<GpxMapProps> = ({ gpxUrl, color = ACCENT, height = 340, onStats, compactProfile = false, profileHeader = true }) => {
  const mapDivRef = useRef<HTMLDivElement>(null);
  const elevWrapRef = useRef<HTMLDivElement>(null);
  const elevCanvasRef = useRef<HTMLCanvasElement>(null);

  const mapInstanceRef = useRef<L.Map | null>(null);
  const hoverMarkerRef = useRef<L.CircleMarker | null>(null);
  const pointsRef = useRef<TrackPoint[]>([]);
  const distsRef = useRef<number[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<{ distanceKm: number; ascentM: number } | null>(null);
  const [hasElevation, setHasElevation] = useState(false);
  const [hover, setHover] = useState<{ idx: number; x: number; y: number } | null>(null);
  // Zaehlt jede geladene Strecke. Das Profil steckt in einem Canvas, also nicht im DOM-Baum:
  // ohne diesen Merker bliebe beim Wechsel auf eine andere Etappe (gleiches `hasElevation`,
  // kein Hover) die alte Zeichnung stehen, waehrend Karte und Text schon die neue zeigen.
  const [dataVersion, setDataVersion] = useState(0);
  // Ueber eine Ref, damit ein neu erzeugter Callback das Laden nicht erneut anstoesst.
  const onStatsRef = useRef(onStats);
  onStatsRef.current = onStats;

  // Load GPX, init map, draw initial elevation
  useEffect(() => {
    let cancelled = false;
    // Der Hover-Index zeigt auf die Punkte der vorigen Strecke — er gilt fuer die neue nicht.
    setError(null);
    setHover(null);

    (async () => {
      try {
        const res = await fetch(gpxUrl);
        if (!res.ok) throw new Error(`GPX fetch failed (${res.status})`);
        const xml = await res.text();
        if (cancelled) return;
        const points = parseGpx(xml);
        if (points.length < 2) throw new Error('GPX hat keine Trackpunkte');

        const latlngs: L.LatLngTuple[] = points.map(p => [p.lat, p.lon]);
        const bounds = L.latLngBounds(latlngs);

        if (mapDivRef.current && !mapInstanceRef.current) {
          // On touch devices one-finger panning hijacks vertical page scroll, making it
          // hard to scroll past the map. Disable dragging there (pinch-zoom + the +/- zoom
          // controls still work); leave full pan/scroll-zoom on pointer (desktop) devices.
          const isTouch = typeof window !== 'undefined'
            && typeof window.matchMedia === 'function'
            && window.matchMedia('(pointer: coarse)').matches;
          const m = L.map(mapDivRef.current, {
            zoomControl: true,
            attributionControl: true,
            dragging: !isTouch,
            // Viertelstufen, damit die Strecke die Karte füllt statt beim nächsten ganzen Zoom klein in der Mitte zu stehen.
            zoomSnap: 0.25,
            // Das Mausrad scrollt die Seite, nicht die Karte: mit Zoom am Rad blieb die Seite
            // stehen, sobald der Zeiger über die Karte kam, und die Karte zoomte stattdessen.
            // Zoomen geht über die Knöpfe, Doppelklick und Ziehen.
            scrollWheelZoom: false,
          });
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 18,
            attribution: '<a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">© OpenStreetMap</a>'
          }).addTo(m);
          // Ohne Leaflet-Präfix (Flagge, „Leaflet“), wie die übrigen Karten.
          m.attributionControl.setPrefix(false);
          L.polyline(latlngs, { color, weight: 4, opacity: 0.95, lineJoin: 'round', lineCap: 'round' }).addTo(m);
          // Start (dark) + end (accent) markers, matching the prototype route treatment.
          const start = latlngs[0];
          const end = latlngs[latlngs.length - 1];
          L.circleMarker(start, { radius: 6, color: '#fff', weight: 2, fillColor: INK, fillOpacity: 1 }).addTo(m);
          L.circleMarker(end, { radius: 6, color: '#fff', weight: 2, fillColor: color, fillOpacity: 1 }).addTo(m);
          m.fitBounds(bounds, { padding: [24, 24] });
          mapInstanceRef.current = m;
        }

        const dists = cumulativeDistancesKm(points);
        const totalKm = dists[dists.length - 1];
        let ascent = 0;
        for (let i = 1; i < points.length; i++) {
          const a = points[i - 1].ele;
          const b = points[i].ele;
          if (a != null && b != null && b > a) ascent += b - a;
        }
        pointsRef.current = points;
        distsRef.current = dists;
        if (!cancelled) {
          setStats({ distanceKm: totalKm, ascentM: ascent });
          setHasElevation(points.filter(p => p.ele != null).length >= 2);
          setDataVersion(v => v + 1);
          onStatsRef.current?.({
            distanceKm: totalKm,
            ascentM: ascent,
            start: { lat: points[0].lat, lon: points[0].lon },
            end: { lat: points[points.length - 1].lat, lon: points[points.length - 1].lon },
          });
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? 'GPX konnte nicht geladen werden');
          setStats(null);
          setHasElevation(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      hoverMarkerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpxUrl, color]);

  // Redraw elevation once the canvas is mounted, on hover and whenever a new track is loaded
  useEffect(() => {
    drawElevation(hover?.idx ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hover, hasElevation, dataVersion]);

  function drawElevation(hoverIdx: number | null) {
    const canvas = elevCanvasRef.current;
    if (!canvas) return;
    const points = pointsRef.current;
    const dists = distsRef.current;
    if (points.length < 2) return;

    const eles = points.map(p => p.ele).filter((e): e is number => e != null);
    if (eles.length < 2) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth;
    const cssH = canvas.clientHeight;
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, cssW, cssH);

    const plotL = ELEV_PAD_LEFT;
    const plotR = cssW - ELEV_PAD_RIGHT;
    const plotT = ELEV_PAD_TOP;
    const plotB = cssH - ELEV_PAD_BOTTOM;
    const plotW = plotR - plotL;
    const plotH = plotB - plotT;

    const minEle = Math.min(...eles);
    const maxEle = Math.max(...eles);
    const totalKm = dists[dists.length - 1];

    // Vertikale Normalisierung: siehe `elevationBand` in @radevents/shared — dieselbe
    // Rechnung nutzt die App, damit eine Strecke dort nicht anders hoch aussieht.
    const { lo: yLo, hi: yHi } = elevationBand(minEle, maxEle, totalKm);

    const xOf = (km: number) => plotL + (km / totalKm) * plotW;
    const yOf = (ele: number) => plotB - ((ele - yLo) / (yHi - yLo)) * plotH;

    // ── Gridlines + axis labels ───────────────────────────────────────────
    ctx.strokeStyle = GRID;
    ctx.lineWidth = 1;
    ctx.fillStyle = AXIS_LABEL;
    ctx.font = AXIS_FONT;

    // Tick-Anzahl an die Plotbreite koppeln, sonst überlappen die km-Labels auf
    // schmalen (mobilen) Charts.
    const kmTicks = Math.max(3, Math.min(9, Math.round(plotW / 70)));
    const kmStep = niceStep(totalKm, kmTicks);
    ctx.textBaseline = 'top';
    for (let km = 0; km <= totalKm + 0.001; km += kmStep) {
      const x = xOf(km);
      ctx.beginPath();
      ctx.moveTo(x, plotT);
      ctx.lineTo(x, plotB);
      ctx.stroke();
      // Erstes Label linksbündig, damit „0 km" nicht am Rand abgeschnitten wird;
      // das letzte rechtsbündig, sonst schiebt es sich über die Höhen-Beschriftung
      // in der rechten Spalte.
      const nearRight = x > plotR - 26;
      ctx.textAlign = km === 0 ? 'left' : nearRight ? 'right' : 'center';
      ctx.fillText(`${Math.round(km)} km`, km === 0 ? plotL : nearRight ? plotR : x, plotB + 3);
    }

    const eleStep = niceStep(yHi - yLo, 3);
    const eleStart = Math.ceil(yLo / eleStep) * eleStep;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    for (let ele = eleStart; ele <= yHi + 0.001; ele += eleStep) {
      const y = yOf(ele);
      ctx.beginPath();
      ctx.moveTo(plotL, y);
      ctx.lineTo(plotR, y);
      ctx.stroke();
      ctx.fillText(`${Math.round(ele)} m`, plotR + 3, y);
    }

    // ── Filled elevation area ─────────────────────────────────────────────
    ctx.beginPath();
    ctx.moveTo(plotL, plotB);
    for (let i = 0; i < points.length; i++) {
      const ele = points[i].ele;
      if (ele == null) continue;
      ctx.lineTo(xOf(dists[i]), yOf(ele));
    }
    ctx.lineTo(plotR, plotB);
    ctx.closePath();
    ctx.fillStyle = AREA_FILL;
    ctx.fill();

    // ── Elevation line ────────────────────────────────────────────────────
    ctx.beginPath();
    let started = false;
    for (let i = 0; i < points.length; i++) {
      const ele = points[i].ele;
      if (ele == null) continue;
      const x = xOf(dists[i]);
      const y = yOf(ele);
      if (!started) { ctx.moveTo(x, y); started = true; } else { ctx.lineTo(x, y); }
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 2.5;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // ── Hover crosshair + dot ─────────────────────────────────────────────
    if (hoverIdx != null && hoverIdx >= 0 && hoverIdx < points.length) {
      const ele = points[hoverIdx].ele;
      const x = xOf(dists[hoverIdx]);

      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, plotT);
      ctx.lineTo(x, plotB);
      ctx.stroke();
      ctx.setLineDash([]);

      if (ele != null) {
        const y = yOf(ele);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }
  }

  // Move the crosshair + map marker to whatever point sits under the given
  // client x/y. Shared by mouse hover and touch/pen dragging (pointer events).
  function updateHoverAt(clientX: number, clientY: number) {
    const canvas = elevCanvasRef.current;
    const points = pointsRef.current;
    const dists = distsRef.current;
    if (!canvas || points.length < 2) return;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const cssW = canvas.clientWidth;
    const totalKm = dists[dists.length - 1];
    const plotL = ELEV_PAD_LEFT;
    const plotR = cssW - ELEV_PAD_RIGHT;
    const t = Math.min(1, Math.max(0, (x - plotL) / (plotR - plotL)));
    const targetKm = t * totalKm;
    // Binary search for the nearest cumulative-distance index
    let lo = 0, hi = dists.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (dists[mid] < targetKm) lo = mid + 1; else hi = mid;
    }
    const idx = (lo > 0 && Math.abs(dists[lo - 1] - targetKm) < Math.abs(dists[lo] - targetKm)) ? lo - 1 : lo;

    setHover({ idx, x, y: clientY - rect.top });

    // Sync map marker
    const map = mapInstanceRef.current;
    if (map) {
      const p = points[idx];
      const latLng: L.LatLngExpression = [p.lat, p.lon];
      if (hoverMarkerRef.current) {
        hoverMarkerRef.current.setLatLng(latLng);
      } else {
        hoverMarkerRef.current = L.circleMarker(latLng, {
          radius: 6, color: '#fff', weight: 2, fillColor: color, fillOpacity: 1
        }).addTo(map);
      }
    }
  }

  function clearHover() {
    setHover(null);
    const map = mapInstanceRef.current;
    if (map && hoverMarkerRef.current) {
      map.removeLayer(hoverMarkerRef.current);
      hoverMarkerRef.current = null;
    }
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    // Capture so a finger/pen that briefly slips off the canvas keeps dragging.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
    updateHoverAt(e.clientX, e.clientY);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    // Mouse fires this on plain hover (no button); touch/pen only while pressed.
    updateHoverAt(e.clientX, e.clientY);
  }

  function handlePointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    // Touch/pen: dropping the finger ends the interaction (mouse keeps hovering).
    if (e.pointerType !== 'mouse') clearHover();
  }

  const hoveredDist = hover && distsRef.current[hover.idx];
  const hoveredEle = hover && pointsRef.current[hover.idx]?.ele;

  return (
    <div style={{ background: '#fff' }}>
      {/* position:relative + z-index:0 confines Leaflet's internal pane/control
          z-indexes (400–1000) to a local stacking context, so modals and the
          sticky header render above the map instead of behind it. */}
      <div ref={mapDivRef} style={{ height, width: '100%', background: MAP_BG, position: 'relative', zIndex: 0 }} />

      {hasElevation && (
        <div style={{ padding: compactProfile ? '6px 12px 4px' : '16px 20px', borderTop: `1px solid ${BORDER}` }}>
          {!compactProfile && profileHeader && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
            <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12, letterSpacing: '0.12em', color: INK }}>
              HÖHENPROFIL
            </span>
            {stats && (
              <span style={{ fontWeight: 700, fontSize: 14, color: INK, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {stats.distanceKm.toFixed(1)} km · {formatElevationMeters(stats.ascentM) ?? '0 hm'}
              </span>
            )}
          </div>}
          <div ref={elevWrapRef} style={{ position: 'relative', marginTop: compactProfile || !profileHeader ? 0 : 14 }}>
            <canvas
              ref={elevCanvasRef}
              // touchAction: 'none' keeps a finger drag on the profile from scrolling
              // the page, so touchscreen dragging feels as smooth as mouse hovering.
              style={{ display: 'block', width: '100%', height: compactProfile ? 72 : profileHeader ? 150 : 120, cursor: 'crosshair', touchAction: 'none' }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onPointerLeave={clearHover}
            />
            {hover && hoveredDist != null && (
              <div
                style={{
                  position: 'absolute',
                  left: Math.min(Math.max(hover.x - 50, 0), (elevWrapRef.current?.clientWidth ?? 0) - 100),
                  top: 4,
                  pointerEvents: 'none',
                  background: INK,
                  color: '#fff',
                  fontFamily: "'Space Mono',monospace",
                  fontSize: 11,
                  padding: '4px 8px',
                  borderRadius: 6,
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {hoveredDist.toFixed(1)} km{hoveredEle != null ? ` · ${Math.round(hoveredEle)} m` : ''}
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <div style={{ padding: '14px 20px', fontFamily: "'Space Mono',monospace", fontSize: 12, letterSpacing: '0.04em', color: '#A82F5A' }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default GpxMap;
